import { defineStore } from 'pinia';
import api from '@/api2/client';
import { useSessionStore } from '@/stores/session';

/**
 * Which patient the app is currently looking at.
 *
 * The legacy app kept this inside `login.phrData`, the blob returned by
 * `getAllData` — one call that fetched an entire patient record graph and
 * cached it in Vuex. The port decomposed that endpoint into per-resource
 * routes, so nothing populated the blob any more, and the 135 places that read
 * `allData.patient.externId` all silently got `null`. That is what produced
 * `GET /patients/null/allergies` and, through the legacy catch-all handlers, an
 * "unexpected snag" alert on almost every action.
 *
 * So the *concept* survives — an app that shows one chart at a time needs to
 * know which one — but its source changes. It is set explicitly here rather than
 * arriving as a side effect of a data fetch, which is also what makes
 * impersonation legible: the acting user and the chart being viewed are two
 * different things, and the legacy blob conflated them.
 */
/**
 * The open chart, remembered for this tab only.
 *
 * `sessionStorage`, not `localStorage`: closing the tab should close the chart.
 * The value is an id and nothing else — no name, no demographics — so a shared
 * machine leaks nothing from storage, and the server decides on every request
 * whether this user may still see it.
 */
const CHART_KEY = 'pacmny.chart.patientId';

function remember(patientId) {
  try {
    if (patientId) sessionStorage.setItem(CHART_KEY, String(patientId));
  } catch (e) { /* private mode; the chart just will not persist */ }
}

function recall() {
  try {
    return sessionStorage.getItem(CHART_KEY);
  } catch (e) {
    return null;
  }
}

function forget() {
  try {
    sessionStorage.removeItem(CHART_KEY);
  } catch (e) { /* nothing to clean up */ }
}

/** Whether a chart id is the signed-in person's own chart. */
function isOwnChart(session, patientId) {
  const own = session.me?.patient_id;

  return own !== null && own !== undefined && String(own) === String(patientId);
}

/**
 * Put the closing of a chart on the record (`DELETE /patients/{id}/chart-view`).
 *
 * Best effort by design: leaving a chart must never be blocked by the record of
 * leaving it — a clinician whose assignment ended mid-visit still gets out.
 */
async function recordExit(patientId) {
  try {
    await api.delete(`/patients/${patientId}/chart-view`);
  } catch (e) { /* the exit already happened in the app; the record is best effort */ }
}

export const usePatientContextStore = defineStore('patientContext', {
  state: () => ({
    /** The selected patient resource, or null when none is in view. */
    patient: null,
    /**
     * True only in an act-as session — one person operating as another.
     *
     * Not "viewing someone else's chart", which is ordinary clinical work.
     */
    impersonating: false,
    /**
     * Whether `initialise()` has run for this session.
     *
     * Distinct from `patient !== null`, because "no chart" is the correct
     * outcome for staff — without this flag the router would re-run the choice
     * on every navigation, and a caller could not tell "not looked yet" from
     * "looked, and there is none".
     */
    initialised: false,
    loading: false,
    error: null,
  }),

  getters: {
    patientId: (state) => state.patient?.id ?? null,

    /**
     * Main's `viewingPatientChart`: a chart is open and it is not the signed-in
     * person's own.
     *
     * Viewing never changes who is acting (decision D4) — the signed-in staff
     * member stays the actor for every permission, attribution and "From" —
     * so this is a statement about what is on screen, not about identity.
     * Main's readers use it for exactly that: the "Viewing:" banner, hiding
     * your own password change, the staff member as sender while charting.
     */
    viewing: (state) => Boolean(state.patient) && !isOwnChart(useSessionStore(), state.patient.id),

    /**
     * The legacy `phrData.patient` shape.
     *
     * `externId` is the field the app keys on in 135 places. It was
     * `account_user.unique_ID`; it is the patient's id now, which route binding
     * accepts alongside the ULID. Both spellings are provided because the
     * legacy code is inconsistent about the capital I.
     */
    legacyShape: (state) => {
      // The managing organization's account number, which the legacy blob
      // carried on the patient as `externId2`. It is a property of the tenant,
      // not of the chart, so it comes from the session — but it has to appear
      // here because roughly forty call sites read it off `patient` and several
      // refuse to proceed without it ("Missing account or user identifier").
      const accountNumber = useSessionStore().organizationNumber;

      const p = state.patient;

      // No chart open, but the tenant is still known. `MyAccount` and the
      // rosters load before any chart is selected and refuse outright on a
      // missing account number, so withholding it here would leave them blank
      // for a reason that has nothing to do with the chart.
      if (!p) {
        return { patient: { externId2: accountNumber, externid2: accountNumber, accountnumber: accountNumber } };
      }

      return {
        patient: {
          // `id` as well as the two `externId` spellings: the newer components
          // reach for the API's field name, the older ones for the legacy one,
          // and they are the same number.
          id: p.id,
          externId: p.id,
          externid: p.id,
          publicId: p.public_id,
          firstName: p.first_name ?? '',
          middleName: p.middle_name ?? '',
          lastName: p.last_name ?? '',
          fname: p.first_name ?? '',
          lname: p.last_name ?? '',
          email: p.email ?? '',
          phone: p.phone ?? '',
          dob: p.date_of_birth ?? null,
          gender: p.gender ?? '',
          mrn: p.medical_record_number ?? '',

          // The profile and demographics screens bind these directly. The
          // legacy blob spelled the mailing address with an `m` prefix and gave
          // the primary one two names, so both are here rather than making the
          // templates choose.
          addr: p.address_line1 ?? '',
          address: p.address_line1 ?? '',
          address2: p.address_line2 ?? '',
          city: p.city ?? '',
          state: p.state ?? '',
          zip: p.postal_code ?? '',
          maddr: p.mailing_address_line1 ?? p.address_line1 ?? '',
          mcity: p.mailing_city ?? p.city ?? '',
          mstate: p.mailing_state ?? p.state ?? '',
          mzip: p.mailing_postal_code ?? p.postal_code ?? '',
          npi: p.npi ?? '',
          suffix: p.suffix ?? '',
          userName: p.username ?? '',
          externId2: accountNumber,
          externid2: accountNumber,
          accountnumber: accountNumber,

          // The profile form has two numbered phone slots, each with a type.
          // The API carries one primary phone plus its type, so slot one is
          // filled and slot two is left for the form to populate — rather than
          // duplicating the same number into both, which is what a naive
          // fallback would do.
          phone1: p.phone ?? '',
          phone1type: p.phone_type ?? '',
          phone2: p.phone_secondary ?? '',
          phone2type: p.phone_secondary_type ?? '',
        },
      };
    },
  },

  actions: {
    /**
     * Put a chart in view. Accepts a resource or an id.
     *
     * Somebody else's chart is *opened* rather than fetched — `POST
     * /patients/{id}/chart-view`, main's `/session/enterPatientChart`. The API
     * checks the chart-entry rule there (`patients:view_chart` and a care
     * relationship or roster reach) and records the opening under the
     * clinician's own name; the answer is the chart header, so it is one round
     * trip either way. One's own chart is simply read. Moving from one chart to
     * another records leaving the first.
     */
    async select(patientOrId) {
      if (!patientOrId) {
        this.clear();
        return null;
      }

      this.loading = true;
      this.error = null;

      const session = useSessionStore();
      const targetId = typeof patientOrId === 'object' ? patientOrId.id : patientOrId;
      const leaving = this.viewing && String(this.patient.id) !== String(targetId) ? this.patient.id : null;

      try {
        if (isOwnChart(session, targetId)) {
          this.patient = typeof patientOrId === 'object'
            ? patientOrId
            : await api.get(`/patients/${targetId}`);
        } else {
          this.patient = await api.post(`/patients/${targetId}/chart-view`);
        }

        if (leaving !== null) {
          recordExit(leaving);
        }

        // Impersonation is an act-as session, not a chart selection.
        //
        // This used to be `session.me.patient_id !== this.patient.id`, which is
        // true for every member of staff looking at any chart — a clinician's
        // entire working day — and produced a standing "administrator:
        // <organization>" banner on every screen.
        //
        // It is exactly the conflation this store's docblock says the port set
        // out to undo: the acting user and the chart being viewed are two
        // different things. Opening somebody's chart is not becoming them.
        //
        // The authority is the server, which mints the act-as token and reports
        // it on `/me`, so this reads the session and nothing else.
        this.impersonating = Boolean(session.isImpersonating);

        remember(this.patient.id);

        return this.patient;
      } catch (e) {
        this.error = e.message;
        this.patient = null;
        // The tab's remembered chart goes with it. Left behind, it names the
        // chart the clinician just left, which a reload reopens without their
        // having chosen it again.
        forget();
        if (leaving !== null) {
          recordExit(leaving);
        }
        throw e;
      } finally {
        this.loading = false;
      }
    },

    /**
     * Close the chart in view and put the closing on the record — main's
     * `/session/exitPatientChart`. `clear()` is the silent version, for a
     * session that is ending anyway.
     */
    async leave() {
      const closing = this.viewing ? this.patient.id : null;

      this.clear();

      if (closing !== null) {
        await recordExit(closing);
      }
    },

    /**
     * Choose a sensible chart at sign-in.
     *
     * A patient has exactly one and should never have to pick it — the legacy
     * app made them land on a chooser showing a single row. Staff get no default:
     * guessing which of their caseload they meant would be worse than showing
     * none, and the patient list is the screen for that.
     */
    async initialise() {
      const session = useSessionStore();

      if (!session.me) return null;

      this.initialised = true;

      if (session.me.patient_id) {
        return this.select(session.me.patient_id);
      }

      // Staff: no default chart, but a reload should not close the one that was
      // open. Only the id is kept, and only for the tab — the API re-authorizes
      // it on every request, so this is a convenience, never a grant. A chart
      // the user may no longer see simply fails to load and we fall back to
      // none, which is the same place they would have started.
      const remembered = recall();

      if (remembered) {
        try {
          return await this.select(remembered);
        } catch (e) {
          forget();
        }
      }

      this.clear();
      return null;
    },

    clear() {
      forget();
      this.patient = null;
      this.impersonating = false;
      // `initialised` is deliberately not reset: clearing a chart is a
      // selection, not a reason to go looking for a default again.
      this.error = null;
    },
  },
});

export default usePatientContextStore;
